import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Printer, RefreshCw, ChevronDown, ChevronUp,
  Users, DollarSign, TrendingUp, UserPlus, Crown, Sparkles, BadgePercent, Trophy, Star,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const PIE_COLORS = ["#D4AF37", "#6DBE45", "#E5C158", "#7FCE57", "#A8862A", "#4F9B2A", "#F5D77A", "#9EE07C"];

function KPI({ icon: Icon, label, value, color = "gold", sub }) {
  const isGold = color === "gold";
  return (
    <div className={`print-card card-luxury ${isGold ? "accent-gold" : "accent-leaf"} p-4`}>
      <div className="flex items-center justify-between">
        <div className={`h-9 w-9 rounded-xl ${isGold ? "bg-gold/15 text-gold print-gold" : "bg-leaf/15 text-leaf print-leaf"} grid place-items-center`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-foreground/50 print-muted">{label}</div>
      <div className={`mt-1 text-xl md:text-2xl font-heading font-light ${isGold ? "print-gold" : "text-leaf print-leaf"}`}>{value}</div>
      {sub && <div className="text-[10px] text-foreground/40 mt-1 print-muted">{sub}</div>}
    </div>
  );
}

export default function DailyReport() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/daily", { params: { date } });
      setData(data);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "ar" ? "ar-SA" : lang === "id" ? "id-ID" : "en-GB", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
    } catch { return iso; }
  };

  function doPrint() {
    setExpanded(true);
    setTimeout(() => window.print(), 250);
  }

  // ── Collapsed compact bar ─────────────────────────────────────────────
  if (!expanded) {
    return (
      <div className="card-luxury accent-gold p-4 flex flex-wrap items-center justify-between gap-3" data-testid="daily-report-collapsed">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gold/15 text-gold grid place-items-center">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gold">{t("daily.title")}</div>
            <div className="text-sm text-foreground/60">
              {data
                ? `${data.visits} ${t("daily.transactions")} · ${formatSAR(data.revenue)}`
                : t("common.loading")}
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(true)}
          data-testid="daily-report-expand"
          className="btn-outline-gold text-sm py-2"
        >
          {t("daily.toggle")} <ChevronDown className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // ── Full infographic ───────────────────────────────────────────────────
  return (
    <div data-print="report" data-testid="daily-report-full">
      <div className="card-luxury p-5 md:p-7 print-card">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5 pb-5 border-b border-gold/20">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold print-gold">
              Java SPA · {t("daily.title")}
            </div>
            <h2 className="mt-1 text-2xl md:text-3xl font-heading">{fmtDate(date)}</h2>
            {data && (
              <div className="text-xs text-foreground/50 mt-1 print-muted">
                {t("daily.generatedBy")} <span className="text-foreground/80">{data.generated_by || user?.name}</span>
                {" · "} {formatDate(data.generated_at)}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 print-hide">
            <input
              type="date"
              data-testid="daily-date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              className="bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm"
            />
            <button onClick={load} data-testid="daily-refresh" className="btn-outline-gold text-sm py-2">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={doPrint} data-testid="daily-print-btn" className="btn-gold text-sm py-2">
              <Printer className="h-4 w-4" /> {t("daily.print")}
            </button>
            <button onClick={() => setExpanded(false)} className="btn-outline-gold text-sm py-2">
              <ChevronUp className="h-4 w-4" /> {t("daily.hide")}
            </button>
          </div>
        </div>

        {loading && !data && <div className="text-center py-12 text-foreground/40">{t("common.loading")}</div>}
        {data && data.visits === 0 && (!data.cancelled || data.cancelled.count === 0) && (
          <div className="text-center py-12 text-foreground/50">
            <div className="text-5xl mb-3">📭</div>
            <div className="text-lg">{t("daily.noData")}</div>
          </div>
        )}

        {/* Cancelled-only state: no active tx but there are cancellations */}
        {data && data.visits === 0 && data.cancelled && data.cancelled.count > 0 && (
          <div className="card-luxury print-card p-4 mt-2 border border-red-500/20 break-inside-avoid" data-testid="daily-cancelled-only">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-heading text-red-400">⊘ {t("daily.cancelledTitle")}</h3>
              <div className="text-xs text-foreground/60 print-muted">
                {data.cancelled.count} {t("daily.transactions")} · <span className="text-red-300">−{formatSAR(data.cancelled.total_amount)}</span>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 print-muted">
                <tr className="border-b border-red-500/20">
                  <th className="text-start py-2">{t("daily.time")}</th>
                  <th className="text-start py-2">{t("receipt.customer")}</th>
                  <th className="text-start py-2">{t("daily.cashier")}</th>
                  <th className="text-start py-2">{t("daily.reason")}</th>
                  <th className="text-end py-2">{t("kasir.total")}</th>
                </tr>
              </thead>
              <tbody>
                {data.cancelled.items.map((c) => {
                  const time = c.created_at ? new Date(c.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" }) : "—";
                  return (
                    <tr key={c.id} className="border-b border-red-500/10 last:border-0 align-top">
                      <td className="py-2 text-foreground/70 print-muted">{time}</td>
                      <td className="py-2">
                        <div className="font-medium">{c.customer_phone}</div>
                        {c.items && c.items.length > 0 && (
                          <div className="text-[11px] text-foreground/50 print-muted line-clamp-2">
                            {c.items.map((it) => it.service_name_id).join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="py-2 text-foreground/70 print-muted">{c.cashier_name || "—"}</td>
                      <td className="py-2 text-foreground/80">
                        <div className="text-xs">{c.cancel_reason || "—"}</div>
                        {c.cancel_notes && (
                          <div className="text-[10px] text-foreground/50 italic mt-0.5 print-muted">"{c.cancel_notes}"</div>
                        )}
                      </td>
                      <td className="py-2 text-end text-red-300 line-through">{formatSAR(c.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.visits > 0 && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI icon={Users} label={t("daily.visits")} value={data.visits} color="gold" />
              <KPI icon={DollarSign} label={t("daily.revenue")} value={formatSAR(data.revenue)} color="gold" />
              <KPI icon={TrendingUp} label={t("daily.avg")} value={formatSAR(data.avg_transaction)} color="leaf" />
              <KPI icon={UserPlus} label={t("daily.newCust")} value={data.new_customers} color="leaf" />
            </div>

            {/* Highlights row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <KPI icon={Crown} label={t("daily.vipVisits")} value={data.vip_visits} color="gold" />
              <KPI icon={Sparkles} label={t("daily.upsells")} value={data.upsell_count} color="leaf" />
              <KPI icon={BadgePercent} label={t("daily.discountGiven")} value={formatSAR(data.discount_total)} color="gold" />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-3 gap-4 mt-6">
              {/* Hourly */}
              <div className="card-luxury print-card p-4 lg:col-span-2">
                <h3 className="text-sm font-heading mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-gold print-gold" /> {t("daily.hourly")}
                </h3>
                <div className="h-56 min-h-[224px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.hourly}>
                      <defs>
                        <linearGradient id="hourFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#6DBE45" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3d3d3d" />
                      <XAxis dataKey="hour" stroke="#75756c" tickFormatter={(h) => `${h}:00`} />
                      <YAxis stroke="#75756c" allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "#1f1f1f", border: "1px solid #3d3d3d", borderRadius: 8 }}
                        formatter={(v, n) => n === "revenue" ? formatSAR(v) : v}
                        labelFormatter={(h) => `${h}:00`}
                      />
                      <Area type="monotone" dataKey="visits" stroke="#D4AF37" strokeWidth={2} fill="url(#hourFill)" name={t("daily.visits")} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Service mix pie */}
              <div className="card-luxury print-card p-4">
                <h3 className="text-sm font-heading mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-leaf print-leaf" /> {t("daily.serviceMix")}
                </h3>
                <div className="h-56 min-h-[224px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.services_breakdown}
                        dataKey="count"
                        nameKey="name"
                        innerRadius={45}
                        outerRadius={85}
                        paddingAngle={2}
                      >
                        {data.services_breakdown.map((s, i) => (
                          <Cell key={s.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#1f1f1f", border: "1px solid #3d3d3d" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 space-y-1 text-xs max-h-24 overflow-y-auto scrollbar-thin">
                  {data.services_breakdown.slice(0, 5).map((s, i) => (
                    <div key={s.name} className="flex justify-between items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="truncate">{s.name}</span>
                      </div>
                      <span className="font-medium shrink-0">{s.count}×</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Therapist performance */}
            <div className="card-luxury print-card p-4 mt-4">
              <h3 className="text-sm font-heading mb-3 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-gold print-gold" /> {t("daily.therapistPerf")}
              </h3>
              <div className="h-56 min-h-[224px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.therapists_breakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3d3d3d" />
                    <XAxis dataKey="name" stroke="#75756c" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#75756c" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#1f1f1f", border: "1px solid #3d3d3d", borderRadius: 8 }}
                      formatter={(v, n) => n === "commission" || n === "revenue" ? formatSAR(v) : v}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="sessions" fill="#6DBE45" radius={[6, 6, 0, 0]} name={t("daily.sessions")} />
                    <Bar dataKey="commission" fill="#D4AF37" radius={[6, 6, 0, 0]} name={t("daily.commission")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Highlights row */}
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              {data.top_therapist && (
                <div className="card-luxury print-card accent-gold p-4 flex items-center gap-4">
                  <Trophy className="h-10 w-10 text-gold print-gold" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 print-muted">{t("daily.topTherapist")}</div>
                    <div className="text-lg font-heading truncate">{data.top_therapist.name}</div>
                    <div className="text-xs text-foreground/60 print-muted">
                      {data.top_therapist.sessions} {t("daily.sessions")} · {formatSAR(data.top_therapist.commission)}
                    </div>
                  </div>
                </div>
              )}
              {data.top_customer && (
                <div className="card-luxury print-card accent-leaf p-4 flex items-center gap-4">
                  <Star className="h-10 w-10 text-leaf print-leaf fill-leaf/20" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 print-muted">{t("daily.topCustomer")}</div>
                    <div className="text-lg font-heading truncate">{data.top_customer.phone}</div>
                    <div className="text-xs text-foreground/60 print-muted">
                      {data.top_customer.visits} {t("daily.transactions")} · {formatSAR(data.top_customer.spent)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cashier table */}
            {data.cashier_breakdown.length > 1 && (
              <div className="card-luxury print-card p-4 mt-4">
                <h3 className="text-sm font-heading mb-3">{t("daily.cashier")}</h3>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 print-muted">
                    <tr className="border-b border-gold/20">
                      <th className="text-start py-2">{t("daily.cashier")}</th>
                      <th className="text-end py-2">{t("daily.transactions")}</th>
                      <th className="text-end py-2">{t("daily.revenue")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cashier_breakdown.map((c) => (
                      <tr key={c.name} className="border-b border-charcoal-600/30 last:border-0">
                        <td className="py-2">{c.name}</td>
                        <td className="py-2 text-end">{c.transactions}</td>
                        <td className="py-2 text-end print-gold text-gold">{formatSAR(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Payment Method breakdown */}
            {data.payment_breakdown && (data.payment_breakdown.cash.count + data.payment_breakdown.card.count > 0) && (
              <div className="grid sm:grid-cols-2 gap-4 mt-4 break-inside-avoid" data-testid="daily-payment-breakdown">
                <div className="card-luxury print-card accent-leaf p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 print-muted">{t("kasir.payCash")}</div>
                    <span className="text-[10px] bg-leaf/15 text-leaf uppercase rounded-full px-2 py-0.5">{data.payment_breakdown.cash.count} tx</span>
                  </div>
                  <div className="mt-2 text-2xl font-heading text-leaf">{formatSAR(data.payment_breakdown.cash.revenue)}</div>
                  <div className="text-[10px] text-foreground/40 print-muted mt-1">
                    {data.revenue > 0 ? `${Math.round((data.payment_breakdown.cash.revenue / data.revenue) * 100)}% dari total pendapatan` : "—"}
                  </div>
                </div>
                <div className="card-luxury print-card accent-gold p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 print-muted">{t("kasir.payCard")}</div>
                    <span className="text-[10px] bg-gold/15 text-gold uppercase rounded-full px-2 py-0.5">{data.payment_breakdown.card.count} tx</span>
                  </div>
                  <div className="mt-2 text-2xl font-heading text-gold">{formatSAR(data.payment_breakdown.card.revenue)}</div>
                  <div className="text-[10px] text-foreground/40 print-muted mt-1">
                    {data.revenue > 0 ? `${Math.round((data.payment_breakdown.card.revenue / data.revenue) * 100)}% dari total pendapatan` : "—"}
                  </div>
                </div>
              </div>
            )}

            {/* Cancelled transactions (failed/refunded) */}
            {data.cancelled && data.cancelled.count > 0 && (
              <div className="card-luxury print-card p-4 mt-4 border border-red-500/20 break-inside-avoid" data-testid="daily-cancelled-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-heading text-red-400">⊘ {t("daily.cancelledTitle")}</h3>
                  <div className="text-xs text-foreground/60 print-muted">
                    {data.cancelled.count} {t("daily.transactions")} · <span className="text-red-300">−{formatSAR(data.cancelled.total_amount)}</span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 print-muted">
                    <tr className="border-b border-red-500/20">
                      <th className="text-start py-2">{t("daily.time")}</th>
                      <th className="text-start py-2">{t("receipt.customer")}</th>
                      <th className="text-start py-2">{t("daily.cashier")}</th>
                      <th className="text-start py-2">{t("daily.reason")}</th>
                      <th className="text-end py-2">{t("kasir.total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cancelled.items.map((c) => {
                      const time = c.created_at ? new Date(c.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" }) : "—";
                      return (
                        <tr key={c.id} className="border-b border-red-500/10 last:border-0 align-top">
                          <td className="py-2 text-foreground/70 print-muted">{time}</td>
                          <td className="py-2">
                            <div className="font-medium">{c.customer_phone}</div>
                            {c.items && c.items.length > 0 && (
                              <div className="text-[11px] text-foreground/50 print-muted line-clamp-2">
                                {c.items.map((it) => it.service_name_id).join(", ")}
                              </div>
                            )}
                          </td>
                          <td className="py-2 text-foreground/70 print-muted">{c.cashier_name || "—"}</td>
                          <td className="py-2 text-foreground/80">
                            <div className="text-xs">{c.cancel_reason || "—"}</div>
                            {c.cancel_notes && (
                              <div className="text-[10px] text-foreground/50 italic mt-0.5 print-muted">"{c.cancel_notes}"</div>
                            )}
                          </td>
                          <td className="py-2 text-end text-red-300 line-through">{formatSAR(c.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer (print only) */}
            <div className="hidden text-center text-xs text-foreground/50 mt-6 pt-4 border-t border-gold/10 print:block print-muted">
              Java SPA Riyadh · King Fahd, Riyadh 11421 · +966 55 222 1465 · Open 24h
            </div>
          </>
        )}
      </div>
    </div>
  );
}
